<script lang="ts" setup>
import Accordion from "~/components/ui/Accordion.vue";
import AccordionTab from "~/components/ui/AccordionTab.vue";
import SuperBoostOutcome from "./SuperBoostOutcome.vue";
import Tooltip from "~/components/ui/Tooltip.vue";
import { IEvent } from "~/types/sports/event";
import { IMarket } from "~/types/sports/market";
import { IOutcome } from "~/types/sports/outcome";
import { IPrice } from "~/types/sports/price";
import slugify from "~/utils/sport/slugify";
import api from "~/utils/auth/api";
import { useQuery } from "@tanstack/vue-query";
import t from "~/utils/translate";
import { mdiInformationOutline } from "@mdi/js";
import { betwayCashOut } from "~/assets/betwayIcons";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import { useBetbook } from "~/store/betbook";

interface IProps {
	event: IEvent;
	market: IMarket | undefined;
	outcomes: IOutcome[];
	outcomesMap: Map<string, IOutcome>;
	prices: IPrice[];
	pricesMap: Map<string, IPrice>;
}

const props = defineProps<IProps>();

const { data: tooltips } = useQuery<{ [x: string]: string }>({
	queryKey: ["moreMarkets-tooltips", props.event.sportId],
	queryFn: () =>
		api({
			url: `/cron/locales/tooltips/synapse/{locale}?tooltipPrefixFilter=${props.event.sportId}`,
			type: "config",
			options: { method: "GET" },
		}),
});

const active = ref(true);
const betbook = useBetbook();

const isSuspended = computed(() => !props.market?.isActive || !betbook.isEventActive(props.event));
const tooltip = computed(() => tooltips.value && tooltips.value[`tooltips.${props.event.sportId}-${slugify(props.market!.name)}`]);
</script>

<template>
	<Accordion>
		<AccordionTab :active="active" @toggle="active = !active">
			<template #header>
				<div
					class="flex items-center justify-between w-full p-2 rounded-t-lg pr-8 bg-dark-700 text-light-50 transition-all"
					:class="{ 'rounded-b-lg': !active }"
				>
					<strong class="text-sm leading-none">{{ market?.displayName }}</strong>
					<div class="flex items-center justify-center">
						<UiIcon
							v-if="market?.isCashOutAllowed"
							:path="betwayCashOut"
							class="mr-1"
							:class="['fill-primary-600  h-6 w-6']"
							viewBox="0 0 470.45 469.95"
						/>
						<Tooltip position="top" :tooltip="tooltip || t('no-market-tooltip')" type="click">
							<UiIcon :path="mdiInformationOutline" path-class="prevent-trigger" class="w-6 h-6 fill-light-50 prevent-trigger" />
						</Tooltip>
					</div>
				</div>
			</template>

			<!-- Body -->
			<div
				class="flex items-center justify-between gap-1 bg-light-50 dark:bg-dark-800 rounded-b-lg p-1"
				:class="{ 'flex-wrap': outcomes.length > 3 }"
			>
				<NuxtLink
					v-if="isSuspended"
					:to="
						sportUrlBuilder({
							fixtureType: 'highlights',
							sport: event.sportId,
							event,
						})
					"
					class="flex items-center justify-center text-dark-800 dark:text-light-50 w-full h-full rounded-lg text-xs p-1 border border-solid border-light-400 dark:border-dark-400 transition-all"
				>
					<strong>{{ t("suspended") }}</strong>
				</NuxtLink>
				<SuperBoostOutcome
					v-else
					v-for="(outcome, i) of outcomes"
					:class="{ 'basis-[calc(50%-2px)]': outcomes.length > 3 }"
					:key="`${i}-${outcome.marketId}`"
					:outcome="outcomesMap.get(outcome.outcomeId)"
					:event="event"
					:market="market"
					:prices="prices"
					:pricesMap="pricesMap"
				/>
			</div>
		</AccordionTab>
	</Accordion>
</template>
